import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";
import * as React from "react";

import { Text } from "src/library/components/Text";
import { ICreditRequestReasonCode } from "src/modules/credit-request/api/credit-request.api";
import {
  CreditStatus,
  ISupportTicket
} from "src/modules/support/api/support.api";
import CreditRequestStatusStyled, { creditStatusTextSwitch } from "src/modules/support/components/CreditStatus";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { ITerminology } from "src/core/shared/api";

export interface ICreditRequestColumns {
  actionButton: (info: CellContext<ISupportTicket, unknown>) => React.ReactNode,
  terminology: ITerminology
  columns?: CreditRequestColumnsPicked[]
  creditRequestReasons: ICreditRequestReasonCode[] | undefined,
  isCreditRequests?: boolean
}

type CreditRequestColumnsPicked = keyof AvailableCreditRequestColumns;

interface AvailableCreditRequestColumns {
  id: ColumnDef<ISupportTicket, unknown>,
  customer: ColumnDef<ISupportTicket, unknown>,
  branch: ColumnDef<ISupportTicket, unknown>,
  orderDeliveryDate: ColumnDef<ISupportTicket, unknown>,
  customerReference: ColumnDef<ISupportTicket, unknown>,
  status: ColumnDef<ISupportTicket, unknown>,
  createdAt: ColumnDef<ISupportTicket, unknown>
  resolvedAt: ColumnDef<ISupportTicket, unknown>
  actions: ColumnDef<ISupportTicket, unknown>
}

export const getAdminCreditRequestTicketsColumns = (params: ICreditRequestColumns) => {
  const customColumnHelper = customCreateColumnHelper<ISupportTicket>();
  const columnHelper = createColumnHelper<ISupportTicket>();

  const allColumns: AvailableCreditRequestColumns = {

    id: customColumnHelper.numberColumn(columnHelper, "id", {
      header: "Ticket Number",
      cell: info => {
        return (
          <Text>
            {`#${info.cell.getValue()}`}
          </Text>
        );
      }
    }),
    customer: customColumnHelper.standardTextColumn(columnHelper, "branch.customer.name", {
      header: "Customer",
      enableSorting: false
    }),
    branch: customColumnHelper.standardTextColumn(columnHelper, "branch", {
      enableSorting: false,
      header: "Delivery Point",
      cell: info => {
        return (
          <Text>
            <>
              {info.row.original.branch?.branchNumber ?? "-"}
            </>
          </Text>
        );
      }
    }),
    orderDeliveryDate: customColumnHelper.dateColumn(columnHelper, "order.orderDeliveryDate", {
      header: "Delivery Date",
      enableSorting: false
    }),
    customerReference: customColumnHelper.standardTextColumn(columnHelper, "order.customerReference", {
      header: "PO Number",
      enableSorting: false
    }),
    status: customColumnHelper.standardTextColumn(columnHelper, "creditStatus", {
      header: "Status",
      enableSorting: false,

      cell: info => {
        return <CreditRequestStatusStyled status={info.row.original.creditStatus as CreditStatus} />;
      }
    }),
    createdAt: customColumnHelper.dateColumn(columnHelper, "createdAt", { header: "Created At" }),
    resolvedAt: customColumnHelper.dateColumn(columnHelper, "resolvedAt", { header: "Resolved At" }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})

  };

  if (!params.isCreditRequests) {
    allColumns.status.meta = {
      filter: {
        filterType: "select",
        options: [
          {
            text: creditStatusTextSwitch(CreditStatus.AutoReviewed),
            value: CreditStatus.AutoReviewed
          },
          {
            text: creditStatusTextSwitch(CreditStatus.Reviewed),
            value: CreditStatus.Reviewed
          }
        ]
      }
    };
  } else {
    allColumns.status.enableColumnFilter = false;
  }

  if (params.columns) {
    return params.columns.map(column => allColumns[ column ]);
  } else {
    return Object.values(allColumns);
  }
};
